import { useEffect, useState, useDeferredValue } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { status } from 'constants/general';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import categoriesStrings from 'modules/categories/strings';
import { submitPriceList, editPriceList } from 'modules/categories/categoriesSlice';
import { onChangeAndValidationPrice } from './DoctorConsultationPriceListModal.helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendLoaderStyle,
  ItemContainer,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendBlueButtonStyle,
  InputField,
  FieldLabel,
  FieldContainer,
  WarningMsg,
} from './DoctorConsultationPriceListModal.style';

const PriceListModal = ({ onClose, priceListRecordInfo, healthPlanId }) => {
  const dispatch = useDispatch();
  const isEdit = !!priceListRecordInfo?.id;
  const submitPriceListState = useSelector(state => state.categories.submitPriceList);
  const editPriceListState = useSelector(state => state.categories.editPriceList);
  const isSubmitting =
    status.SUBMITING === submitPriceListState.status ||
    status.SUBMITING === editPriceListState.status;
  const isFailed = status.FAIL === submitPriceList.status || status.FAIL === editPriceList.status;

  const [priceListInfo, setPriceListInfo] = useState({
    healthPlanId,
    priceListName: priceListRecordInfo?.name || '',
    priceListPrice: priceListRecordInfo?.priceListConfigurations[0]?.priceValue,
    priceListKey: priceListRecordInfo?.priceListKey || '',
    priceListActivationStatus: priceListRecordInfo?.active,
    priceListId: priceListRecordInfo?.id,
  });

  const onClickClose = () => {
    onClose();
  };

  const prevSubmitPriceListStateStatus = useDeferredValue(submitPriceListState.status);
  const prevEditPriceListStateStatus = useDeferredValue(editPriceListState.status);
  useEffect(() => {
    if (
      (status.SUCCESS === submitPriceListState.status &&
        prevSubmitPriceListStateStatus !== submitPriceListState.status) ||
      (status.SUCCESS === editPriceListState.status &&
        editPriceListState.status !== prevEditPriceListStateStatus)
    ) {
      onClickClose();
    }
  }, [submitPriceListState.status, editPriceListState.status]);

  const validatePriceListData = () => {
    let isValid = true;
    if (!priceListInfo.priceListName.trim() || !(priceListInfo.priceListPrice >= 0))
      isValid = false;
    return isValid;
  };

  const onSave = () => {
    if (isEdit) {
      // Update action
      const payload = {
        id: priceListInfo.priceListId,
        data: {
          healthPlanId: Number(priceListInfo.healthPlanId),
          name: priceListInfo.priceListName,
          active: priceListInfo.priceListActivationStatus,
        },
      };
      dispatch(editPriceList(payload));
    } else {
      // create action
      const payload = {
        healthPlanId: Number(priceListInfo.healthPlanId),
        name: priceListInfo.priceListName,
        priceListTypeId: 1,
        categoryId: 1,
        priceListConfigurations: [
          {
            priceValue: Number(priceListInfo.priceListPrice),
          },
        ],
      };
      dispatch(submitPriceList(payload));
    }
  };

  return (
    <Popup showCloseBtn={false}>
      <PopupWrapper>
        <PopupTitleContainer>
          <PopupTitleWrapper>
            <PopupTitle>
              {isEdit
                ? categoriesStrings.priceLists.editPriceList
                : categoriesStrings.priceLists.createAPriceList}
            </PopupTitle>
            <PopupTitleSpan
              onClick={() => {
                onClickClose();
              }}
            />
          </PopupTitleWrapper>
        </PopupTitleContainer>

        <Container>
          <div
            style={{
              width: '680px',
            }}
          >
            <div>
              <ItemContainer>
                <FieldContainer>
                  <FieldLabel>Price list name</FieldLabel>
                  <InputField
                    id="pricelist-name"
                    placeholder="Price lists name"
                    value={priceListInfo.priceListName}
                    onChange={e => {
                      setPriceListInfo({
                        ...priceListInfo,
                        priceListName: e.target.value,
                      });
                    }}
                  />
                </FieldContainer>
                <FieldContainer>
                  <FieldLabel>Consultation Fee</FieldLabel>
                  <InputField
                    disabled={isEdit}
                    id="price-login-input"
                    value={priceListInfo.priceListPrice}
                    placeholder="0.00"
                    onChange={e => {
                      onChangeAndValidationPrice(e.target.value, () =>
                        setPriceListInfo({
                          ...priceListInfo,
                          priceListPrice: e.target.value,
                        }),
                      );
                    }}
                  />
                </FieldContainer>
                {isFailed && <WarningMsg>Something went wrong !</WarningMsg>}
              </ItemContainer>
            </div>
            <ButtonsContainer>
              <Button
                onClick={() => {
                  onClickClose();
                }}
                btnText="Cancel"
                extendButtonStyle={extendGrayButtonStyle}
                key="dismiss-popup"
                disabled={isSubmitting === status.SUBMITING}
              />
              <Button
                onClick={() => {
                  onSave();
                }}
                btnText={
                  isEdit
                    ? categoriesStrings.priceLists.editPriceList
                    : categoriesStrings.priceLists.createAPriceList
                }
                extendButtonStyle={extendBlueButtonStyle}
                key="save-btn"
                disabled={!validatePriceListData()}
                extendLoaderStyle={extendLoaderStyle}
                isLoading={isSubmitting}
              />
            </ButtonsContainer>
          </div>
        </Container>
      </PopupWrapper>
    </Popup>
  );
};

PriceListModal.propTypes = {
  onClose: PropTypes.func,
  priceListKey: PropTypes.string,
  priceListRecordInfo: PropTypes.shape({
    priceListName: PropTypes.string,
    priceListPrice: PropTypes.number,
    priceListKey: PropTypes.string,
  }),
};

export default PriceListModal;
